// Supply Vendor Updates cluster (MySubmittedUpdates + SubmitNewUpdate)
// strings — EN + TR.
//
// Wave-4 Day 4. Cross-page shared dict across:
// - features/supply/MySubmittedUpdates.tsx (vendor update list, Aybars SO3
//   submitter persona view)
// - features/supply/SubmitNewUpdate.tsx (master catalog update suggestion
//   submission form: H1 "Yeni öneri gönder" + "Gönderiliyor…" hardcoded TR
//   → bilingual)
//
// Aybars SO3 / Furkan SO3 persona TR-friendly preserved (NO seller-leak
// constraint — supply IS the supplier perspective).
//
// Status enum source: mocks/data/vendor-update-suggestions
// VendorUpdateSuggestionStatus (4 values: pending, approved, rejected,
// propagated).
//
// Wave-1 helpers consume:
// - lib/datetime.ts (formatDate) — date locale

import type { LocalizedDict } from '../index'

type SupplyVendorUpdatesStrings = {
  mySubmittedUpdates: {
    pageTitle: string
    description: string
    nonSo3Gate: {
      prefix: string
      so3Strong: string
      personaActiveLabel: string
      hint: string
    }
    table: {
      columnHeaders: {
        product: string
        proposedChange: string
        status: string
        submittedAt: string
        adminNotes: string
      }
      emptyState: {
        prefix: string
        cta: string
        suffix: string
      }
    }
    statusBadges: {
      pending: string
      approved: string
      rejected: string
      propagated: string
    }
    actions: { refresh: string; newUpdate: string; openDetail: string }
    states: {
      loading: string
      error: string
    }
    adminNotesFormat: (n: number) => string
  }
  submitNewUpdate: {
    pageTitle: string
    description: {
      prefix: string
      approvedToken: string
      orMid: string
      rejectedToken: string
      suffix: string
    }
    backLink: string
    nonSo3Gate: {
      prefix: string
      so3Strong: string
      personaActiveLabel: string
      hint: string
    }
    fields: {
      productName: { label: string; placeholder: string }
      productId: { label: string; placeholder: string }
      fieldName: { label: string; placeholder: string }
      oldValue: { label: string; placeholder: string }
      newValue: { label: string; placeholder: string }
      reason: { label: string; placeholder: string }
    }
    requiredMark: string
    actions: { submit: string; submitting: string; clear: string }
    errorPrefix: string
    toasts: {
      successTitle: string
      successDescFormat: (productName: string) => string
      adminTitle: string
      adminDescFormat: (vendorName: string, productName: string) => string
      errorTitle: string
    }
  }
}

export const SUPPLY_VENDOR_UPDATES_STRINGS: LocalizedDict<SupplyVendorUpdatesStrings> = {
  en: {
    mySubmittedUpdates: {
      pageTitle: 'My Submitted Updates',
      description:
        'Master catalog update suggestions I have sent and the admin review status.',
      nonSo3Gate: {
        prefix: 'Master catalog update suggestion is only available for ',
        so3Strong: 'SO3 Vendor',
        personaActiveLabel: "personas. Current active persona:",
        hint: 'Switch to an SO3 persona (Aybars Ahşap).',
      },
      table: {
        columnHeaders: {
          product: 'Product',
          proposedChange: 'Proposed change',
          status: 'Status',
          submittedAt: 'Submitted',
          adminNotes: 'Admin notes',
        },
        emptyState: {
          prefix: 'No suggestions submitted yet.',
          cta: 'Submit the first suggestion',
          suffix: '.',
        },
      },
      statusBadges: {
        pending: 'Admin reviewing',
        approved: 'Approved',
        rejected: 'Rejected',
        propagated: 'Reflected in master catalog',
      },
      actions: {
        refresh: 'Refresh',
        newUpdate: '+ New suggestion',
        openDetail: 'Open detail',
      },
      states: {
        loading: 'Loading…',
        error: 'List could not be loaded.',
      },
      adminNotesFormat: (n) => `${n} note${n === 1 ? '' : 's'}`,
    },
    submitNewUpdate: {
      pageTitle: 'New suggestion',
      description: {
        prefix:
          'Suggest a field update to the master catalog. When you submit, it lands in the admin queue; if Volkan A1 approves it becomes ',
        approvedToken: 'approved',
        orMid: ', if rejected it becomes ',
        rejectedToken: 'rejected',
        suffix: '.',
      },
      backLink: '← My Submitted Updates',
      nonSo3Gate: {
        prefix: 'Master catalog update suggestion is only available for ',
        so3Strong: 'SO3 Vendor',
        personaActiveLabel: "personas. Current active persona:",
        hint: "Switch to an SO3 persona from the persona switcher (Aybars Ahşap).",
      },
      fields: {
        productName: {
          label: 'Product name',
          placeholder: 'XL Laser-engraved Wooden Cheese Board',
        },
        productId: {
          label: 'Product ID',
          placeholder: 'prod_wood_engr_xl',
        },
        fieldName: {
          label: 'Field name',
          placeholder: 'price_usd',
        },
        oldValue: {
          label: 'Current value',
          placeholder: '42.00',
        },
        newValue: {
          label: 'New value',
          placeholder: '47.50',
        },
        reason: {
          label: 'Reason',
          placeholder: 'Laser tuning cost for new size +13%',
        },
      },
      requiredMark: '*',
      actions: {
        submit: 'Submit suggestion',
        submitting: 'Submitting…',
        clear: 'Clear',
      },
      errorPrefix: 'Error:',
      toasts: {
        successTitle: 'Suggestion submitted',
        successDescFormat: (productName) =>
          `${productName} — landed in the admin queue.`,
        adminTitle: 'New vendor suggestion',
        adminDescFormat: (vendorName, productName) =>
          `${vendorName} → ${productName}`,
        errorTitle: 'Could not be submitted',
      },
    },
  },
  tr: {
    mySubmittedUpdates: {
      pageTitle: 'Gönderdiğim Öneriler',
      description:
        'Master kataloğa gönderdiğim güncelleme önerileri ve admin değerlendirme durumu.',
      nonSo3Gate: {
        prefix: 'Master katalog güncelleme önerisi sadece ',
        so3Strong: 'SO3 Vendor',
        personaActiveLabel: "persona'ları için. Şu an aktif persona:",
        hint: "SO3 persona'sına geç (Aybars Ahşap).",
      },
      table: {
        columnHeaders: {
          product: 'Ürün',
          proposedChange: 'Önerilen değişiklik',
          status: 'Durum',
          submittedAt: 'Gönderim',
          adminNotes: 'Admin notları',
        },
        emptyState: {
          prefix: 'Henüz öneri gönderilmedi.',
          cta: 'İlk öneriyi gönder',
          suffix: '.',
        },
      },
      statusBadges: {
        pending: 'Admin değerlendiriyor',
        approved: 'Onaylandı',
        rejected: 'Reddedildi',
        propagated: 'Master kataloğa yansıdı',
      },
      actions: {
        refresh: 'Yenile',
        newUpdate: '+ Yeni öneri gönder',
        openDetail: 'Detay aç',
      },
      states: {
        loading: 'Yükleniyor…',
        error: 'Liste yüklenemedi.',
      },
      adminNotesFormat: (n) => `${n} not`,
    },
    submitNewUpdate: {
      pageTitle: 'Yeni öneri gönder',
      description: {
        prefix:
          'Master kataloğa bir alan güncellemesi öner. Gönderdiğinde admin kuyruğuna düşer; Volkan A1 onaylarsa ',
        approvedToken: 'approved',
        orMid: ', reddederse ',
        rejectedToken: 'rejected',
        suffix: ' olur.',
      },
      backLink: '← Gönderdiğim Öneriler',
      nonSo3Gate: {
        prefix: 'Master katalog güncelleme önerisi sadece ',
        so3Strong: 'SO3 Vendor',
        personaActiveLabel: "persona'ları için. Şu an aktif persona:",
        hint: "Persona switcher'dan SO3'e geç (Aybars Ahşap).",
      },
      fields: {
        productName: {
          label: 'Ürün adı',
          placeholder: 'Lazerli XL Ahşap Peynir Tahtası',
        },
        productId: {
          label: 'Ürün ID',
          placeholder: 'prod_wood_engr_xl',
        },
        fieldName: {
          label: 'Alan adı',
          placeholder: 'price_usd',
        },
        oldValue: {
          label: 'Mevcut değer',
          placeholder: '42.00',
        },
        newValue: {
          label: 'Yeni değer',
          placeholder: '47.50',
        },
        reason: {
          label: 'Gerekçe',
          placeholder: 'Yeni ebatta laser ayar maliyeti +%13',
        },
      },
      requiredMark: '*',
      actions: {
        submit: 'Öneriyi gönder',
        submitting: 'Gönderiliyor…',
        clear: 'Temizle',
      },
      errorPrefix: 'Hata:',
      toasts: {
        successTitle: 'Öneri gönderildi',
        successDescFormat: (productName) =>
          `${productName} — admin kuyruğuna düştü.`,
        adminTitle: 'Yeni tedarikçi önerisi',
        adminDescFormat: (vendorName, productName) =>
          `${vendorName} → ${productName}`,
        errorTitle: 'Gönderilemedi',
      },
    },
  },
}
